import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'

import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { useLazyChangePlacesQuery, useLazyChangePriceQuery } from 'api/calc'
import { ADORNMENTS, COLORS } from 'config/constants'
import { useFormattedDecimalInput } from 'utils/hooks'
const PRICE_DECIMALS = 2
const UNIT_PRICE_DECIMALS = 3

const enum PRICE_SWITCH_TYPE {
  UNIT_PRICE = 'unit_price',
  PRICE = 'price',
}

type Props = {
  index: number
  row: CalculationDocumentDraftProduct
  onChange: (index: number, field: keyof CalculationDocumentDraftProduct, value: string) => void
  onPatch: (index: number, patch: Partial<CalculationDocumentDraftProduct>) => void
  onPriceModeChange: (index: number, mode: 'unit_price' | 'price') => void
  fieldError: (field: string) => boolean
}

const inactiveStyle = {
  '.MuiOutlinedInput-notchedOutline': {
    borderStyle: 'dotted',
  },
  '.MuiOutlinedInput-input': {
    color: COLORS.LIGHT_TEXT_DISABLED,
  },
  '.MuiInputLabel-root': {
    color: COLORS.LIGHT_TEXT_DISABLED,
  },
}

const DocumentImportCommonFields = ({
  index,
  row,
  onChange,
  onPatch,
  onPriceModeChange,
  fieldError,
}: Props) => {
  const { t } = useTranslation()
  const decimalInput = useFormattedDecimalInput()
  const [ changePriceRequest ] = useLazyChangePriceQuery()
  const [ changePlacesRequest ] = useLazyChangePlacesQuery()

  const currentSwitch = row.price_input_mode
    ?? (row.price && !row.unit_price ? PRICE_SWITCH_TYPE.PRICE : PRICE_SWITCH_TYPE.UNIT_PRICE)

  const [ isUpdating, setIsUpdating ] = useState(false)

  const pieces = row.pieces ?? undefined
  const unitPrice = row.unit_price != null ? Number(row.unit_price) : undefined
  const price = row.price != null ? Number(row.price) : undefined

  const requestChangePrice = async (field: 'unit_price' | 'price' | 'pieces') => {
    if (!pieces) {
      return
    }

    if (field === 'unit_price' && unitPrice != null) {
      const data = await changePriceRequest({ pieces, unit_price: unitPrice }).unwrap()
      onPatch(index, {
        price: data.price_total ?? data.price,
        unit_price: data.unit_price_total ?? data.unit_price,
      })
    }

    if (field === 'price' && price != null) {
      const data = await changePriceRequest({ pieces, price }).unwrap()
      onPatch(index, {
        price: data.price_total ?? data.price,
        unit_price: data.unit_price_total ?? data.unit_price,
      })
    }

    if (field === 'pieces') {
      if (unitPrice != null && currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE) {
        const data = await changePriceRequest({ pieces, unit_price: unitPrice }).unwrap()
        onPatch(index, {
          price: data.price_total ?? data.price,
          unit_price: data.unit_price_total ?? data.unit_price,
        })
      } else if (price != null && currentSwitch === PRICE_SWITCH_TYPE.PRICE) {
        const data = await changePriceRequest({ pieces, price }).unwrap()
        onPatch(index, {
          price: data.price_total ?? data.price,
          unit_price: data.unit_price_total ?? data.unit_price,
        })
      }
    }
  }

  const requestChangePlaces = async (
    field: 'pieces_in_cartoon' | 'places_manual' | 'pieces',
  ) => {
    const pieces_in_cartoon = row.pieces_in_cartoon
    const places = row.places_manual

    if (field === 'pieces_in_cartoon' && places && pieces_in_cartoon != null) {
      if (pieces) {
        const data = await changePlacesRequest({ pieces_in_cartoon, pieces }).unwrap()
        onPatch(index, { places_manual: data.places_total })
      } else {
        const data = await changePlacesRequest({ pieces_in_cartoon, places }).unwrap()
        onPatch(index, { pieces: data.pieces_total })
      }
      await requestChangePrice('pieces')
      return
    }

    if (field === 'places_manual' && pieces_in_cartoon != null) {
      if (pieces && places != null) {
        const data = await changePlacesRequest({ pieces, places }).unwrap()
        onPatch(index, { pieces_in_cartoon: data.pieces_in_cartoon_total })
      } else if (places != null) {
        const data = await changePlacesRequest({ pieces_in_cartoon, places }).unwrap()
        onPatch(index, { pieces: data.pieces_total })
      }
      await requestChangePrice('pieces')
      return
    }

    if (field === 'pieces' && pieces_in_cartoon != null && pieces != null) {
      const data = await changePlacesRequest({ pieces_in_cartoon, pieces }).unwrap()
      onPatch(index, { places_manual: data.places_total })
      await requestChangePrice('pieces')
    } else if (field === 'pieces' && places != null && pieces != null) {
      const data = await changePlacesRequest({ places, pieces }).unwrap()
      onPatch(index, { pieces_in_cartoon: data.pieces_in_cartoon_total })
      await requestChangePrice('pieces')
    }
  }

  const handleChangePrice = async (field: 'unit_price' | 'price' | 'pieces') => {
    setIsUpdating(true)
    try {
      await requestChangePrice(field)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleChangePlaces = async (field: 'pieces_in_cartoon' | 'places_manual' | 'pieces') => {
    setIsUpdating(true)
    try {
      await requestChangePlaces(field)
    } finally {
      setIsUpdating(false)
    }
  }

  const unitPriceFieldKey = `import-${index}-unit_price`
  const priceFieldKey = `import-${index}-price`

  return (
    <>
      <div className='add-product__common document-import__common-fields'>
        <TextField
          className='add-product__pieces'
          size='small'
          value={row.pieces ?? ''}
          error={fieldError('pieces')}
          variant='outlined'
          label={t('common__text__totalQuantity')}
          InputLabelProps={{ shrink: true }}
          disabled={isUpdating}
          InputProps={{
            endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>,
          }}
          onChange={(event) => onChange(index, 'pieces', event.target.value)}
          onBlur={() => handleChangePlaces('pieces')}
        />
        <TextField
          className='add-product__places_manual'
          size='small'
          value={row.places_manual ?? ''}
          error={fieldError('places_manual')}
          variant='outlined'
          label={t('common__text__numberOfPlaces')}
          InputLabelProps={{ shrink: true }}
          disabled={isUpdating}
          InputProps={{
            endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>,
          }}
          onChange={(event) => onChange(index, 'places_manual', event.target.value)}
          onBlur={() => handleChangePlaces('places_manual')}
        />
        <TextField
          className='add-product__pieces_in_cartoon'
          size='small'
          value={row.pieces_in_cartoon ?? ''}
          error={fieldError('pieces_in_cartoon')}
          variant='outlined'
          label={t('calc__text__packaging__piecesInCartoon')}
          InputLabelProps={{ shrink: true }}
          disabled={isUpdating}
          InputProps={{
            endAdornment: <InputAdornment position='end'>{t('units__text__items')}</InputAdornment>,
          }}
          onChange={(event) => onChange(index, 'pieces_in_cartoon', event.target.value)}
          onBlur={() => handleChangePlaces('pieces_in_cartoon')}
        />
      </div>
      <div className='add-product__switch document-import__price-switch'>
        <FormControl sx={{
          width: '100%',
          '.MuiFormGroup-root': {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
          },
        }}
        >
          <RadioGroup
            value={currentSwitch}
            onChange={(event) => {
              onPriceModeChange(index, event.target.value as 'unit_price' | 'price')
            }}
          >
            <FormControlLabel
              value={PRICE_SWITCH_TYPE.UNIT_PRICE}
              control={<Radio checked={currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE} />}
              sx={{ alignItems: 'start', marginLeft: 0, '.MuiRadio-root': { padding: '8px' } }}
              label={
                <div className='add-product__switch-container'>
                  <Typography variant='body2' sx={{ paddingTop: '10px', color: COLORS.BLACK }}>
                    {t('common__text__pricePerPiece')}
                  </Typography>
                  <TextField
                    className={cx('add-product__price-field', {
                      'add-product__price-field_disabled': currentSwitch === PRICE_SWITCH_TYPE.PRICE,
                    })}
                    size='small'
                    value={decimalInput.getDisplayValue({
                      fieldName: unitPriceFieldKey,
                      rawValue: row.unit_price ?? '',
                      decimals: UNIT_PRICE_DECIMALS,
                    })}
                    onChange={(event) => {
                      decimalInput.startEditing(unitPriceFieldKey)
                      onChange(index, 'unit_price', event.target.value.replace(',', '.'))
                    }}
                    onBlur={() => {
                      decimalInput.stopEditing(unitPriceFieldKey)
                      handleChangePrice('unit_price')
                    }}
                    error={fieldError('unit_price') && currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE}
                    variant='outlined'
                    label={t('common__text__pricePerPiece')}
                    InputLabelProps={{ shrink: true }}
                    disabled={isUpdating || currentSwitch === PRICE_SWITCH_TYPE.PRICE}
                    InputProps={{ endAdornment: ADORNMENTS.PRICE }}
                    sx={currentSwitch !== PRICE_SWITCH_TYPE.UNIT_PRICE ? inactiveStyle : undefined}
                  />
                </div>
              }
            />
            <FormControlLabel
              value={PRICE_SWITCH_TYPE.PRICE}
              control={<Radio checked={currentSwitch === PRICE_SWITCH_TYPE.PRICE} />}
              sx={{ alignItems: 'start', marginLeft: 0, '.MuiRadio-root': { padding: '8px' } }}
              label={
                <div className='add-product__switch-container'>
                  <Typography variant='body2' sx={{ paddingTop: '10px', color: COLORS.BLACK }}>
                    {t('common__text__total')}
                  </Typography>
                  <TextField
                    className={cx('add-product__price-field', {
                      'add-product__price-field_disabled': currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE,
                    })}
                    size='small'
                    value={decimalInput.getDisplayValue({
                      fieldName: priceFieldKey,
                      rawValue: row.price ?? '',
                      decimals: PRICE_DECIMALS,
                    })}
                    onChange={(event) => {
                      decimalInput.startEditing(priceFieldKey)
                      onChange(index, 'price', event.target.value.replace(',', '.'))
                    }}
                    onBlur={() => {
                      decimalInput.stopEditing(priceFieldKey)
                      handleChangePrice('price')
                    }}
                    error={fieldError('unit_price') && currentSwitch === PRICE_SWITCH_TYPE.PRICE}
                    variant='outlined'
                    label={t('common__text__total')}
                    InputLabelProps={{ shrink: true }}
                    disabled={isUpdating || currentSwitch === PRICE_SWITCH_TYPE.UNIT_PRICE}
                    InputProps={{ endAdornment: ADORNMENTS.PRICE }}
                    sx={currentSwitch !== PRICE_SWITCH_TYPE.PRICE ? inactiveStyle : undefined}
                  />
                </div>
              }
            />
          </RadioGroup>
        </FormControl>
      </div>
    </>
  )
}

export default DocumentImportCommonFields
